import { message } from 'antd';
import { useState } from 'react';
import { TFunction, useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { yupResolver } from '@hookform/resolvers/yup';
import { useSendMessage } from 'all-in-one-shared-api';
import { PrimaryButton } from 'components';
import { FormInput, FormTextArea, FormWrapper } from 'components/form';
import { StyledTitle } from 'components/StyledTitle';
import { useProfileInfoContext } from 'contexts/profileInfo';
import { useKeycloakReady } from 'hooks';
import { MessageType } from 'models/MessageType';
import { FormProvider, useForm } from 'react-hook-form';
import styled from 'styled-components';
import { breakpoint } from 'styled-components-breakpoint';
import { object, string } from 'yup';

const Wrapper = styled.div`
  padding: 1rem;

  ${breakpoint('desktop')`
    width: 50%;
  `}
`;

const FormItemWrapper = styled.div`
  display: grid;
  row-gap: 24px;
`;

interface SendInboxMessageFormState {
  receiptTitle: string;
  title: string;
  message: string;
}

const getSendInboxMessageSchema = (t: TFunction) =>
  object({
    receiptTitle: string(),
    title: string(),
    message: string().required(t('common.error.requiredField')),
  });

const SendInboxMessagePage = () => {
  const { token } = useKeycloakReady();
  const { t } = useTranslation();
  const { state } = useProfileInfoContext();

  const methods = useForm<SendInboxMessageFormState>({
    resolver: yupResolver(getSendInboxMessageSchema(t)),
  });

  const [submit, setSubmit] = useState(false);
  const navigate = useNavigate();

  const { isValidating } = useSendMessage({
    accessToken: token ?? '',
    memberId: state?.memberId ?? '',
    memberType: state?.memberType ?? '',
    policyNumber: state?.policyNumber ?? '',
    ...methods.getValues(),
    stop: !submit,
    config: {
      onSuccess(response) {
        setSubmit(false);
        if (response.success) {
          navigate('/admin/send-inbox-msg/success');
        }
      },
      onError() {
        message[MessageType.ERROR](t('common.error.generic'));
        setSubmit(false);
      },
      shouldRetryOnError: false,
    },
  });

  const onSubmit = () => {
    setSubmit(true);
  };

  return (
    <Wrapper>
      <StyledTitle level={2}>{t('admin.sendInboxMessage.title')}</StyledTitle>
      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(onSubmit)}>
          <FormItemWrapper>
            <FormWrapper name={'receiptTitle'} label={t('admin.sendInboxMessage.receiptTitle')}>
              <FormInput
                name={'receiptTitle'}
                placeholder={state && `${state?.firstName} ${state?.lastName}`}
                disabled
              />
            </FormWrapper>
            <FormWrapper name={'title'} label={t('admin.sendInboxMessage.msgTitle')}>
              <FormInput name={'title'} placeholder={t('admin.sendInboxMessage.titlePlaceholder')} />
            </FormWrapper>
            <FormWrapper name={'message'} label={t('admin.sendInboxMessage.content')}>
              <FormTextArea
                name={'message'}
                showCount
                maxLength={500}
                rows={7}
                placeholder={t('admin.sendInboxMessage.messageContentPlaceholder')}
              />
            </FormWrapper>
            <div>
              <PrimaryButton disabled={isValidating} htmlType="submit">
                {t('admin.sendInboxMessage.sendBtn')}
              </PrimaryButton>
            </div>
          </FormItemWrapper>
        </form>
      </FormProvider>
    </Wrapper>
  );
};

export default SendInboxMessagePage;
